import type { FieldMapping, FieldTarget } from "@shared/leadIntake";
import { useTranslate } from "ra-core";

import type { DisqualifyReason, LeadIntakeSettings, WorkItem } from "../types";

// Standardoppsettet for diskvalifiseringsårsaker (kap. 6.7), som migreringen
// legger i disqualify_reasons. Demodataene og testene bruker samme liste.
export const DEFAULT_DISQUALIFY_REASONS: DisqualifyReason[] = [
  {
    id: 1,
    value: "spam",
    label: "Spam",
    retention: "30d",
    hidden: false,
    position: 1,
  },
  {
    id: 2,
    value: "feil_henvendelse",
    label: "Feil henvendelse / ikke kunde",
    retention: "12m",
    hidden: false,
    position: 2,
  },
  {
    id: 3,
    value: "jobbsoknad_leverandor",
    label: "Jobbsøknad / leverandør",
    retention: "12m",
    hidden: false,
    position: 3,
  },
  {
    id: 4,
    value: "utenfor_omraade",
    label: "Utenfor vårt område",
    retention: "12m",
    hidden: false,
    position: 4,
  },
  {
    id: 5,
    value: "duplikat",
    label: "Duplikat",
    retention: "12m",
    hidden: false,
    position: 5,
  },
  {
    id: 6,
    value: "ikke_henvendelse",
    label: "Ikke en henvendelse",
    retention: "30d",
    hidden: false,
    position: 6,
  },
  {
    id: 7,
    value: "ikke_oppgitt",
    label: "Ikke oppgitt (migrert)",
    retention: "12m",
    hidden: true,
    position: 7,
  },
];

// Standardverdiene for inntaket (kap. 6.4, 7.1). Listene er tomme: alt
// instansspesifikt settes i Innstillinger (AK34).
export const DEFAULT_LEAD_INTAKE: Required<LeadIntakeSettings> = {
  leadAddress: "",
  defaultOwnerId: null,
  maxPerSenderPerHour: 3,
  maxPerHour: 20,
  maxLinks: 3,
  replyCutoffHour: 14,
  ignoredDomains: [],
  ignoredSenders: [],
  freeEmailDomains: [],
  spamWords: [],
};

const numberOr = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const listOr = (value: unknown) =>
  Array.isArray(value)
    ? value.map((v) => String(v).trim()).filter(Boolean)
    : [];

// Lagrede innstillinger med standardverdier for det som mangler.
export const resolveLeadIntakeSettings = (
  settings?: LeadIntakeSettings | null,
): Required<LeadIntakeSettings> => {
  const s = settings ?? {};
  const d = DEFAULT_LEAD_INTAKE;
  return {
    leadAddress: s.leadAddress?.trim() ?? "",
    defaultOwnerId: s.defaultOwnerId ?? null,
    maxPerSenderPerHour: numberOr(s.maxPerSenderPerHour, d.maxPerSenderPerHour),
    maxPerHour: numberOr(s.maxPerHour, d.maxPerHour),
    maxLinks: numberOr(s.maxLinks, d.maxLinks),
    replyCutoffHour: numberOr(s.replyCutoffHour, d.replyCutoffHour),
    ignoredDomains: listOr(s.ignoredDomains),
    ignoredSenders: listOr(s.ignoredSenders),
    freeEmailDomains: listOr(s.freeEmailDomains),
    spamWords: listOr(s.spamWords),
  };
};

// Feltene i et standard Formspree-skjema (kap. 6.2). Brukes bare som forslag
// når administrator lager en ny Formspree-kobling.
export const FORMSPREE_SENDER_DOMAIN = "formspree.io";

export const FORMSPREE_FIELD_MAP: FieldMapping[] = [
  { field: "name", target: "contact_name" },
  { field: "email", target: "contact_email" },
  { field: "phone", target: "contact_phone" },
  { field: "company", target: "company_name" },
  { field: "nettside", target: "company_website" },
  { field: "tjeneste", target: "service" },
  { field: "budsjett", target: "budget" },
  { field: "message", target: "message" },
  { field: "samtykke", target: "consent" },
  { field: "side", target: "source_page" },
];

// Norske navn på målfeltene i feltkoblingen (i18n-nøkkel
// crm.settings.form_connections.target.<mål>).
export const FIELD_TARGET_FALLBACK: Record<FieldTarget, string> = {
  contact_name: "Kontakt: navn",
  contact_email: "Kontakt: e-post",
  contact_phone: "Kontakt: telefon",
  company_name: "Kunde: navn",
  company_website: "Kunde: nettsted",
  service: "Tjeneste",
  budget: "Budsjett",
  message: "Melding (første notat)",
  consent: "Samtykke",
  source_page: "Side (kildedetalj)",
  ignore: "Ignorer",
};

export const useFieldTargetLabel = () => {
  const translate = useTranslate();
  return (target: FieldTarget) =>
    translate(`crm.settings.form_connections.target.${target}`, {
      _: FIELD_TARGET_FALLBACK[target] ?? target,
    });
};

// Oppgaver laget av e-postinntaket (kap. 6.3 regel 5): de har innsendingen
// eller en automasjonsnøkkel som begynner på «intake:». Gir «Gjør til lead».
export const isIntakeTask = (
  task: Pick<WorkItem, "inbound_submission_id" | "automation_key">,
) =>
  task.inbound_submission_id != null ||
  !!task.automation_key?.startsWith("intake:");

// Innstillingsskjemaet holder listene som tekst (komma eller linjeskift) og
// standard eier som tekst ("" = ingen).
export type LeadIntakeFormValues = {
  leadAddress: string;
  defaultOwnerId: string;
  maxPerSenderPerHour: number;
  maxPerHour: number;
  maxLinks: number;
  replyCutoffHour: number;
  ignoredDomains: string;
  ignoredSenders: string;
  freeEmailDomains: string;
  spamWords: string;
};

/** «a.no, b.no» eller én per linje ⇄ ["a.no", "b.no"]. */
export const listFromText = (text: unknown) => [
  ...new Set(
    String(text ?? "")
      .split(/[,\n]/)
      .map((v) => v.trim())
      .filter(Boolean),
  ),
];

export const leadIntakeFormValues = (
  settings?: LeadIntakeSettings | null,
): LeadIntakeFormValues => {
  const s = resolveLeadIntakeSettings(settings);
  return {
    leadAddress: s.leadAddress,
    defaultOwnerId: s.defaultOwnerId == null ? "" : String(s.defaultOwnerId),
    maxPerSenderPerHour: s.maxPerSenderPerHour,
    maxPerHour: s.maxPerHour,
    maxLinks: s.maxLinks,
    replyCutoffHour: s.replyCutoffHour,
    ignoredDomains: s.ignoredDomains.join("\n"),
    ignoredSenders: s.ignoredSenders.join("\n"),
    freeEmailDomains: s.freeEmailDomains.join("\n"),
    spamWords: s.spamWords.join("\n"),
  };
};

const numberFromForm = (value: unknown, fallback: number) => {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : fallback;
};

export const leadIntakeFromForm = (
  values?: Partial<LeadIntakeFormValues> | null,
): LeadIntakeSettings => {
  const v = values ?? {};
  const d = DEFAULT_LEAD_INTAKE;
  const owner = v.defaultOwnerId == null ? "" : String(v.defaultOwnerId);
  return {
    leadAddress: String(v.leadAddress ?? "")
      .trim()
      .toLowerCase(),
    defaultOwnerId: owner === "" ? null : Number(owner),
    maxPerSenderPerHour: numberFromForm(
      v.maxPerSenderPerHour,
      d.maxPerSenderPerHour,
    ),
    maxPerHour: numberFromForm(v.maxPerHour, d.maxPerHour),
    maxLinks: numberFromForm(v.maxLinks, d.maxLinks),
    replyCutoffHour: Math.min(
      23,
      numberFromForm(v.replyCutoffHour, d.replyCutoffHour),
    ),
    ignoredDomains: listFromText(v.ignoredDomains).map((x) =>
      x.toLowerCase().replace(/^@/, ""),
    ),
    ignoredSenders: listFromText(v.ignoredSenders).map((x) => x.toLowerCase()),
    freeEmailDomains: listFromText(v.freeEmailDomains).map((x) =>
      x.toLowerCase().replace(/^@/, ""),
    ),
    spamWords: listFromText(v.spamWords),
  };
};
